import React from 'react';
import { Header } from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

const PAICapitalPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold text-black mb-8 leading-tight">
                Investing in the companies that define the future.
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                At PAI Capital, we are future-focused investors committed to backing category-defining companies at the forefront of technological transformation. We partner with ambitious founders who are building the breakthroughs that will reshape industries—and human capability—for decades to come.
              </p>
              <p className="text-xl text-gray-600 leading-relaxed">
                Our investment strategy centers on three rapidly accelerating sectors that we believe will create the next trillion-dollar opportunities:
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/aa8e441ee6b3dcd76e85530900cc4728.jpg"
                alt="PAI Capital Office"
                className="w-full h-auto rounded-lg shadow-lg object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Culture Quote Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <blockquote className="text-white">
                <p className="text-2xl font-light italic mb-6">
                  "A culture is not a set of beliefs, it's a set of actions."
                </p>
                <cite className="text-sm uppercase tracking-wider text-gray-400">
                  Our Philosophy
                </cite>
              </blockquote>
            </div>
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">Who We Are</h2>
              <p className="text-lg text-gray-300 leading-relaxed">
                Our culture is our way of doing things, not just reading about them. We are clear about our beliefs and what we stand for. We define how we treat entrepreneurs, our relationships within our networks, and each other.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Focus Areas */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Investment Focus</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We focus on three key areas where we see the greatest potential for transformative impact
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-robot-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-black mb-4">🚀 Artificial Intelligence</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                AI is becoming the foundational layer of every industry.
              </p>
              <div className="text-left">
                <p className="text-gray-600 text-sm leading-relaxed">
                  We seek companies with exponential potential—businesses positioned not only to benefit from AI adoption, but to become critical infrastructure for the global AI economy.
                </p>
              </div>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-robot-2-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-black mb-4">🤖 Robotics & Automation</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Robotics is entering its "deployment decade." Driven by advances in AI reasoning, perception models, and low-cost hardware.
              </p>
              <p className="text-gray-600">
                We invest in teams building the systems that will augment human productivity, redefine labor, and expand the boundaries of what machines can do.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-rocket-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-black mb-4">🛰️ Space & Frontier Technologies</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Space is transforming from a government-dominated sector into a commercially scalable frontier.
              </p>
              <div className="text-left">
                <p className="text-gray-600 text-sm leading-relaxed">
                  We believe space will unlock new economic zones, global real-time data flows, and an ecosystem of interplanetary applications previously unimaginable.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Build the Future?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            We're looking for exceptional founders building transformative companies. 
            If you're ready to change the world, we want to hear from you.
          </p>
          <Button variant="ghost" size="lg" className="border-white text-white hover:bg-white hover:text-black">
            Submit Your Pitch
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PAICapitalPage;
